import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { cx } from './clases';

export interface AccionMenu {
  id: string;
  /** Lo que se lee en el menú. */
  texto: string;
  /** Nombre accesible si el texto solo no basta («Suplente en desarrollo: PAULINA RÍOS»). */
  aria?: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Acción que quita algo («Quitar apoyo»): en rojo. */
  peligro?: boolean;
}

interface Props {
  /** Nombre accesible del botón ⋮ («Más acciones: PAULINA RÍOS»). */
  etiqueta: string;
  acciones: AccionMenu[];
  /** Alto del botón: 36 en la tabla, 44 en la tarjeta del teléfono. */
  tamano?: 36 | 44;
  className?: string;
  /** Apagado (la fila es de una tienda que se está dejando): no abre, y si estaba abierto se cierra. */
  disabled?: boolean;
}

const Puntos = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="12" cy="5" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="12" cy="19" r="1.6" />
  </svg>
);

/**
 * El menú ⋮ de una fila (diseño: «Suplente en desarrollo», «Historial»,
 * «Quitar apoyo»). Propio y no `TieredMenu`/`Menu` de PrimeReact:
 *
 * - el `Menu` emergente de PrimeReact 10 lee un contexto que la app no tiene
 *   (no hay `PrimeReactProvider`) y en navegador real lanzaba al cerrarse;
 * - se pinta con `position: fixed` junto al botón, así que ni el panel de la
 *   tabla ni el pie fijo lo recortan, y vive DENTRO de `.metas-ui` (no hace
 *   falta `CLASE_OVERLAY`).
 *
 * Accesible: botón con `aria-haspopup="menu"` y `aria-expanded`; el menú es un
 * `role="menu"` de `menuitem`, se recorre con las flechas, Escape lo cierra y
 * devuelve el foco al botón, y un clic fuera también lo cierra.
 */
/** Margen del menú contra los bordes de la ventana. */
const MARGEN = 8;

/**
 * Dónde va el menú (`position: fixed`): junto al botón, alineado a su derecha,
 * hacia arriba si abajo no cabe, y nunca fuera de la ventana por la izquierda
 * (`anchoMenu` = 0 mientras no se ha medido).
 */
export function posicionMenu(
  boton: { top: number; bottom: number; right: number },
  ventana: { ancho: number; alto: number },
  anchoMenu = 0,
): React.CSSProperties {
  const { ancho, alto } = ventana;
  const arriba = alto > 0 && boton.bottom + 220 > alto && boton.top > 220;
  const junto = Math.max(MARGEN, ancho - boton.right);
  const tope = anchoMenu > 0 && ancho > 0 ? Math.max(MARGEN, ancho - anchoMenu - MARGEN) : junto;
  return {
    position: 'fixed',
    right: Math.min(junto, tope),
    ...(arriba ? { bottom: Math.max(MARGEN, alto - boton.top + 4) } : { top: boton.bottom + 4 }),
  };
}

const ventana = () => ({ ancho: window.innerWidth || 0, alto: window.innerHeight || 0 });

export const MenuAcciones: React.FC<Props> = ({ etiqueta, acciones, tamano = 36, className, disabled = false }) => {
  const [abierto, setAbierto] = useState(false);
  // Nace `fixed` y oculto: nunca entra en el flujo junto al botón.
  const [pos, setPos] = useState<React.CSSProperties>({ position: 'fixed', top: 0, right: 0, visibility: 'hidden' });
  const boton = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();

  const cerrar = useCallback((devolverFoco = false) => {
    setAbierto(false);
    if (devolverFoco) boton.current?.focus();
  }, []);

  // ⚠️ La posición se mide AL ABRIR, antes de pintar el menú. Medida después
  // (en un efecto) veía el botón DESPLAZADO: el menú, sin `fixed` todavía,
  // entraba en el flujo junto al botón y lo empujaba; al pasar a `fixed` el
  // botón volvía a su sitio y el menú quedaba ~226 px a su izquierda (en el
  // teléfono, 70 px fuera de la pantalla).
  const alternar = () => {
    if (disabled) return;
    if (abierto) {
      setAbierto(false);
      return;
    }
    if (boton.current) setPos(posicionMenu(boton.current.getBoundingClientRect(), ventana()));
    setAbierto(true);
  };

  useEffect(() => {
    if (disabled) setAbierto(false);
  }, [disabled]);

  // Ya pintado (y antes de que se vea), se mide el MENÚ para no salirse por la izquierda.
  useLayoutEffect(() => {
    if (!abierto || !boton.current || !menu.current) return;
    const anchoMenu = menu.current.getBoundingClientRect().width;
    if (anchoMenu > 0) setPos(posicionMenu(boton.current.getBoundingClientRect(), ventana(), anchoMenu));
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    const primero = menu.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])');
    primero?.focus();
    const fuera = (e: Event) => {
      const t = e.target as Node;
      if (menu.current?.contains(t) || boton.current?.contains(t)) return;
      cerrar();
    };
    const alMover = () => cerrar();
    document.addEventListener('mousedown', fuera);
    document.addEventListener('touchstart', fuera);
    window.addEventListener('resize', alMover);
    window.addEventListener('scroll', alMover, true);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('touchstart', fuera);
      window.removeEventListener('resize', alMover);
      window.removeEventListener('scroll', alMover, true);
    };
  }, [abierto, cerrar]);

  const alTeclear = (e: React.KeyboardEvent) => {
    const items = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? []);
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      cerrar(true);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(i + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(i - 1 + items.length) % items.length]?.focus();
    } else if (e.key === 'Tab') {
      cerrar();
    }
  };

  if (!acciones.length) return null;

  return (
    <>
      <button
        ref={boton}
        type="button"
        aria-label={etiqueta}
        aria-haspopup="menu"
        aria-expanded={abierto && !disabled}
        aria-controls={abierto && !disabled ? id : undefined}
        onClick={alternar}
        disabled={disabled}
        className={cx(
          'metas-foco inline-flex shrink-0 items-center justify-center rounded-metas-control border border-transparent bg-transparent text-metas-texto-3 hover:bg-metas-cabecera disabled:cursor-not-allowed disabled:opacity-50',
          // El ⋮ de 36 px, a 44 en pantalla táctil (auditoría del 22-oct-2026).
          tamano === 44 ? 'h-metas-tactil w-metas-tactil' : 'h-9 w-9 [@media(pointer:coarse)]:h-metas-tactil [@media(pointer:coarse)]:w-metas-tactil',
          className,
        )}
      >
        <Puntos />
      </button>
      {abierto && !disabled && (
        <div
          ref={menu}
          id={id}
          role="menu"
          aria-label={etiqueta}
          onKeyDown={alTeclear}
          style={pos}
          className="z-50 flex min-w-[14rem] max-w-[calc(100vw-1rem)] flex-col rounded-metas-control border border-metas-borde bg-metas-panel py-1 shadow-lg"
        >
          {acciones.map((a) => (
            <button
              key={a.id}
              type="button"
              role="menuitem"
              aria-label={a.aria}
              disabled={a.disabled}
              onClick={() => {
                cerrar(true);
                a.onSelect();
              }}
              className={cx(
                'metas-foco flex min-h-[40px] items-center px-4 text-left text-sm hover:bg-metas-cabecera disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-metas-tactil [@media(pointer:coarse)]:min-h-metas-tactil',
                a.peligro ? 'text-metas-error' : 'text-metas-texto',
              )}
            >
              {a.texto}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default MenuAcciones;
